import { useEffect, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { Message } from '@/types/chat';

interface MessageTraceIndicatorProps {
  message: Message;
  onInspect: () => void;
}

// After this many ms with no trace, assume aggregation failed / will not arrive
const AGGREGATION_TIMEOUT_MS = 15000;

export function MessageTraceIndicator({ message, onInspect }: MessageTraceIndicatorProps) {
  const isAI = message.direction === 'OUTGOING' && message.role?.toUpperCase() === 'AI';

  const messageAgeMs = Date.now() - new Date(message.createdAt).getTime();
  const [aggregationExpired, setAggregationExpired] = useState(
    messageAgeMs > AGGREGATION_TIMEOUT_MS,
  );

  useEffect(() => {
    if (aggregationExpired || message.executionTrace) return;
    const remaining = Math.max(0, AGGREGATION_TIMEOUT_MS - messageAgeMs);
    const timer = setTimeout(() => setAggregationExpired(true), remaining);
    return () => clearTimeout(timer);
  }, [aggregationExpired, message.executionTrace, messageAgeMs]);

  if (!isAI) return null;

  const trace = message.executionTrace;
  const toolCount = trace?.tools?.length ?? 0;
  const totalDuration = trace?.tools?.reduce((sum, tool) => sum + (tool.durationMs ?? 0), 0) ?? 0;

  // No trace yet — distinguish "still aggregating" from "won't arrive"
  if (!trace) {
    return (
      <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
        {aggregationExpired ? (
          <>
            <span>—</span>
            <span>Sem trace disponível</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-5 px-2 text-xs text-primary hover:text-primary hover:bg-primary/10"
              onClick={onInspect}
              aria-label="Inspecionar mensagem"
            >
              Inspecionar
              <ChevronRight className="h-3 w-3 ml-1" />
            </Button>
          </>
        ) : (
          <>
            <span className="animate-pulse">⏳</span>
            <span>Agregando trace…</span>
          </>
        )}
      </div>
    );
  }

  // No tools were called
  if (toolCount === 0) {
    return (
      <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
        <span>—</span>
        <span>Sem tools chamadas</span>
      </div>
    );
  }

  // Show trace indicator with button
  return (
    <div className="flex items-center gap-2 mt-2">
      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        <span>⚡</span>
        <span>
          {toolCount} {toolCount === 1 ? 'tool' : 'tools'} · {totalDuration}ms
        </span>
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="h-5 px-2 text-xs text-primary hover:text-primary hover:bg-primary/10"
        onClick={onInspect}
        aria-label="Inspecionar trace de execução"
      >
        Inspecionar
        <ChevronRight className="h-3 w-3 ml-1" />
      </Button>
    </div>
  );
}
